import React, { useMemo, useCallback, useState } from 'react';
import { useTranslation } from 'localization';
import type {
  ApproachExplorerOrganismProps,
  ApproachTreeNode,
} from 'types/approachExplorer';
import { buildApproachTree } from '../../../utils/buildApproachTree';
import { useApproachExplorerState } from '../../../hooks/useApproachExplorerState';
import ApproachTreeMolecule from '../../molecules/ApproachTree/ApproachTreeMolecule';
import ApproachNodeDetailMolecule from '../../molecules/ApproachNodeDetail/ApproachNodeDetailMolecule';
import './ApproachExplorerOrganism.scss';

const CATEGORY_KEYS = ['types', 'contexts', 'flow', 'children', 'factors', 'orbit', 'inputs', 'subapproaches'] as const;

const ApproachExplorerOrganism: React.FC<ApproachExplorerOrganismProps> = ({
  approach,
  dataSource,
  mode = 'view',
  onNodeSelect,
  onNodeAction: _onNodeAction,
  defaultSelectedNodeId,
  defaultExpandedNodeIds,
  className,
}) => {
  const { t } = useTranslation();

  const categoryLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    for (const key of CATEGORY_KEYS) {
      labels[key] = t(`marketplace.approachExplorer.categories.${key}`);
    }
    return labels;
  }, [t]);

  const rootTree = useMemo(
    () =>
      buildApproachTree({
        systemName: approach.systemName,
        name: approach.name,
        description: approach.description,
        definition: approach.definition,
      }, approach.arcTemplate, undefined, 'root', categoryLabels),
    [approach.systemName, approach.name, approach.description, approach.definition, approach.arcTemplate, categoryLabels]
  );

  const {
    tree,
    expandedNodeIds,
    selectedNodeId,
    loadingNodeIds,
    errorNodeIds,
    toggleNode,
    selectNode,
    findNode,
  } = useApproachExplorerState({
    rootTree,
    dataSource,
    defaultExpandedNodeIds,
    defaultSelectedNodeId,
    categoryLabels,
  });

  const selectedNode = useMemo<ApproachTreeNode | undefined | null>(() => {
    if (!selectedNodeId) return null;
    return findNode(selectedNodeId);
  }, [selectedNodeId, findNode]);

  const treeNodes = useMemo(() => {
    if (!tree.children) return [];

    const applyLoadingErrorState = (node: ApproachTreeNode): ApproachTreeNode => {
      const isLoading = loadingNodeIds.has(node.id);
      const errorMsg = errorNodeIds.get(node.id);
      const needsUpdate = isLoading !== !!node.loading || errorMsg !== node.error;

      const updatedChildren = node.children?.map(applyLoadingErrorState);
      const childrenChanged = updatedChildren && node.children &&
        updatedChildren.some((c, i) => c !== node.children![i]);

      if (!needsUpdate && !childrenChanged) return node;

      return {
        ...node,
        loading: isLoading || undefined,
        error: errorMsg,
        children: updatedChildren ?? node.children,
      };
    };

    return tree.children.map(applyLoadingErrorState);
  }, [tree.children, loadingNodeIds, errorNodeIds]);

  const [mobileDetailVisible, setMobileDetailVisible] = useState(false);

  const handleSelect = useCallback(
    (nodeId: string) => {
      selectNode(nodeId);
      setMobileDetailVisible(true);
      if (onNodeSelect) {
        const node = findNode(nodeId);
        if (node) onNodeSelect(node);
      }
    },
    [selectNode, findNode, onNodeSelect]
  );

  const handleMobileBack = useCallback(() => {
    setMobileDetailVisible(false);
  }, []);

  const classes = ['approach-explorer-organism', className].filter(Boolean).join(' ');

  const detailClasses = [
    'approach-explorer-organism__detail-panel',
    mobileDetailVisible && 'approach-explorer-organism__detail-panel--visible',
  ].filter(Boolean).join(' ');

  return (
    <div className={classes} data-testid="approach-explorer" data-mode={mode}>
      <div className="approach-explorer-organism__tree-panel">
        <ApproachTreeMolecule
          nodes={treeNodes}
          expandedNodeIds={expandedNodeIds}
          selectedNodeId={selectedNodeId}
          onToggle={toggleNode}
          onSelect={handleSelect}
        />
      </div>
      <div className={detailClasses}>
        <button
          className="approach-explorer-organism__detail-back"
          onClick={handleMobileBack}
          aria-label={t('marketplace.approachExplorer.backToTree')}
        >
          <i className="pi pi-arrow-left" />
          {t('marketplace.approachExplorer.backToTree')}
        </button>
        <ApproachNodeDetailMolecule node={selectedNode ?? undefined} />
      </div>
    </div>
  );
};

ApproachExplorerOrganism.displayName = 'ApproachExplorerOrganism';

export default ApproachExplorerOrganism;
